import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  Schedule,
  Receipt,
  LocationOn,
  Person,
  Phone,
  Email,
  Close
} from '@mui/icons-material';
import MainService from '../services/MainService';
import config from '../services/config.json';

const MyBookings = () => {
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState('');

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const customerId = userData.CustomerId;

  const loadBookings = async () => {
    if (!customerId) {
      setError('Customer ID not found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await MainService.myBookings(customerId);
      if (response && Array.isArray(response)) {
        setBookings(response);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'success';
      case 'in transit':
      case 'intransit':
        return 'warning';
      case 'processing':
        return 'info';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle />;
      case 'in transit':
      case 'intransit':
        return <LocalShipping />;
      case 'processing':
        return <Schedule />;
      default:
        return <Receipt />;
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedBooking(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#2E7D32' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
        My Bookings
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        Track your order history and delivery status
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {bookings.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Receipt sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            No orders yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Start shopping to see your order history here.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="/products"
            sx={{
              backgroundColor: '#2E7D32',
              px: 4,
              py: 1.5,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#1B5E20',
              },
            }}
          >
            Start Shopping
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {bookings.map((booking) => (
            <Grid item xs={12} key={booking.Id}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Order #{booking.BookingNo}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Placed on {new Date(booking.Date).toLocaleDateString()}
                      </Typography>
                      {booking.DeliveryDate && (
                        <Typography variant="body2" color="text.secondary">
                          Delivery: {new Date(booking.DeliveryDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      icon={getStatusIcon(booking.Status)}
                      label={booking.Status}
                      color={getStatusColor(booking.Status)}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Order Items Summary */}
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Order Items ({booking.BookingDetails?.length || 0} items)
                  </Typography>
                  {booking.BookingDetails?.slice(0, 3).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>
                        {item.Product?.Name} x {item.Quantity}
                      </Typography>
                      <Typography>₹{item.Total?.toFixed(2)}</Typography>
                    </Box>
                  ))}
                  {booking.BookingDetails?.length > 3 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      +{booking.BookingDetails.length - 3} more items
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Order Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                      ₹{booking.FinalPrice?.toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewDetails(booking)}
                      sx={{
                        borderColor: '#2E7D32',
                        color: '#2E7D32',
                        '&:hover': {
                          borderColor: '#1B5E20',
                          backgroundColor: 'rgba(46, 125, 50, 0.04)',
                        },
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Booking Details Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Order Details #{selectedBooking?.BookingNo}
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box>
              {/* Order Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Order Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Order Number</Typography>
                    <Typography variant="body1">{selectedBooking.BookingNo}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      icon={getStatusIcon(selectedBooking.Status)}
                      label={selectedBooking.Status}
                      color={getStatusColor(selectedBooking.Status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Order Date</Typography>
                    <Typography variant="body1">{new Date(selectedBooking.Date).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Delivery Date</Typography>
                    <Typography variant="body1">
                      {selectedBooking.DeliveryDate ? new Date(selectedBooking.DeliveryDate).toLocaleDateString() : 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Payment Mode</Typography>
                    <Typography variant="body1">{selectedBooking.PaymentMode}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                    <Typography variant="body1">{selectedBooking.IsPaid ? 'Paid' : 'Pending'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Customer Info */}
              {selectedBooking.Customer && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Customer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person color="action" />
                        <Typography variant="body1">{selectedBooking.Customer.Name}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone color="action" />
                        <Typography variant="body1">{selectedBooking.Customer.MobileNo}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email color="action" />
                        <Typography variant="body1">{selectedBooking.Customer.EmailId}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Delivery Address */}
              {selectedBooking.CustomerAddress && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Delivery Address
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOn color="action" sx={{ mt: 0.5 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {selectedBooking.CustomerAddress.Location}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedBooking.CustomerAddress.Address}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedBooking.CustomerAddress.City}
                        {selectedBooking.CustomerAddress.POBox && `, PO Box: ${selectedBooking.CustomerAddress.POBox}`}
                      </Typography>
                      <Chip
                        label={selectedBooking.CustomerAddress.Type}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Order Items */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Order Items
                </Typography>
                <List>
                  {selectedBooking.BookingDetails?.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar
                          src={config.API_BASE_URL + item.Product?.Image1}
                          alt={item.Product?.Name}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.Product?.Name}
                        secondary={`Quantity: ${item.Quantity} | Price: ₹${item.Price} | Total: ₹${item.Total}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Order Summary */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Order Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                    <Typography variant="body1">₹{selectedBooking.SubTotal?.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Delivery Charge</Typography>
                    <Typography variant="body1">₹{selectedBooking.TotalDeliveryCharge?.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                      Total: ₹{selectedBooking.FinalPrice?.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
                {selectedBooking.Remarks && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">Remarks</Typography>
                    <Typography variant="body1">{selectedBooking.Remarks}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyBookings; 